import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, MoreHorizontal, ExternalLink, Search, X, Download, Pencil, AlertCircle, MapPin, Wifi, WifiOff, Navigation } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import DeliveryPhotoView from "@/components/DeliveryPhotoView";
import ProofPhotoView from "@/components/ProofPhotoView";
import AdminOrderEditModal from "@/components/admin/AdminOrderEditModal";
import { formatCHF, formatDateTime, STATUS_LABEL, STATUS_STEPS } from "@/lib/format";

const FILTERS = ["all", ...STATUS_STEPS, "cancelled"];

// Phase 43.19.1 — Filtri operativi propagati dal Centro Operativo Admin
// Ogni preset specifica:
//  - status_param: filtro server-side (mappa a STATUS_STEPS)
//  - client_filter: funzione client-side applicata sopra
//  - label: testo header
const OPS_FILTERS = {
  pending_payment: {
    label: "Pagamenti in attesa di completamento",
    status_param: "pending",
    client_filter: (o, now) => {
      if (o.payment_status !== "pending" || o.status !== "pending") return false;
      const created = new Date(o.created_at).getTime();
      return now - created > 30 * 60 * 1000;
    },
  },
  no_driver: {
    label: "Ordini pagati senza driver assegnato",
    status_param: "pending",
    client_filter: (o, now) => {
      if (o.payment_status !== "paid" || o.status !== "pending") return false;
      if (o.driver_id) return false;
      const paid = o.paid_at ? new Date(o.paid_at).getTime() : null;
      return paid ? now - paid > 15 * 60 * 1000 : true;
    },
  },
  stale: {
    label: "Ordini fermi (driver inattivo)",
    status_param: null, // multi-status: query senza filter, poi client-side
    client_filter: (o, now) => {
      if (!["accepted", "picking_up"].includes(o.status)) return false;
      if (o.picked_up_at) return false;
      if (!o.accepted_at) return false;
      return now - new Date(o.accepted_at).getTime() > 20 * 60 * 1000;
    },
  },
  late: {
    label: "Ordini in ritardo",
    status_param: null,
    client_filter: (o, now) => {
      if (!["picked_up", "delivering"].includes(o.status)) return false;
      if (o.delivered_at) return false;
      if (!o.picked_up_at) return false;
      return now - new Date(o.picked_up_at).getTime() > 90 * 60 * 1000;
    },
  },
};

export default function AdminOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const opsFilter = searchParams.get("filter") || "";
  const focusId = searchParams.get("focus") || "";
  const opsPreset = OPS_FILTERS[opsFilter] || null;

  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState(opsPreset?.status_param || "all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [drawerOrder, setDrawerOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [drivers, setDrivers] = useState([]);
  // Phase 44.31.19 — toggle "Includi archiviati" (default OFF)
  const [includeCleanup, setIncludeCleanup] = useState(searchParams.get("cleanup") === "1");

  const customerIdFilter = searchParams.get("customer_id") || "";
  const driverIdFilter = searchParams.get("driver_id") || "";

  const load = async () => {
    setLoading(true);
    const params = filter === "all" ? {} : { status: filter };
    // Phase 44.31.11 — deep link da scheda cliente/driver
    if (customerIdFilter) params.customer_id = customerIdFilter;
    if (driverIdFilter) params.driver_id = driverIdFilter;
    if (includeCleanup) params.include_cleanup = "true";
    try {
      const { data } = await api.get("/admin/orders", { params });
      setOrders(data);
      // Sync l'ordine aperto col fresh data (se è ancora nella lista)
      if (drawerOrder) {
        const fresh = data.find((o) => o.id === drawerOrder.id);
        if (fresh) setDrawerOrder(fresh);
      }
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, customerIdFilter, driverIdFilter, includeCleanup]);

  // Phase 43.19.1 — Re-sync filtro server quando cambia il preset operativo
  useEffect(() => {
    if (opsPreset && opsPreset.status_param) setFilter(opsPreset.status_param);
    if (opsPreset && !opsPreset.status_param) setFilter("all");
  }, [opsFilter]); // eslint-disable-line

  // Phase 43.19.1 — Apri automaticamente il drawer dell'ordine specificato in ?focus=
  useEffect(() => {
    if (!focusId || !orders.length) return;
    const target = orders.find((o) => o.id === focusId);
    if (target && (!drawerOrder || drawerOrder.id !== focusId)) {
      setDrawerOrder(target);
      // Pulisce solo ?focus= preservando ?filter=
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }
  }, [focusId, orders]); // eslint-disable-line

  const clearOpsFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    next.delete("focus");
    setSearchParams(next, { replace: true });
  };

  // Phase 43.17 — drivers list per riassegnazione admin
  useEffect(() => {
    api.get("/admin/users", { params: { role: "driver" } })
      .then(({ data }) => setDrivers(data || []))
      .catch(() => setDrivers([]));
  }, []);

  // Phase 43.16.1 — Ricerca client-side su tutta la tabella ordini
  // Phase 43.19.1 — Applica anche eventuale opsPreset.client_filter sopra alla search
  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    let list = orders;
    if (opsPreset && typeof opsPreset.client_filter === "function") {
      list = list.filter((o) => opsPreset.client_filter(o, now));
    }
    if (!q) return list;
    return list.filter((o) => {
      return (
        (o.id || "").toLowerCase().includes(q) ||
        (o.customer_name || "").toLowerCase().includes(q) ||
        (o.customer_email || "").toLowerCase().includes(q) ||
        (o.driver_name || "").toLowerCase().includes(q) ||
        (o.pickup_address || "").toLowerCase().includes(q) ||
        (o.delivery_address || "").toLowerCase().includes(q) ||
        (o.pickup_contact_name || "").toLowerCase().includes(q) ||
        (o.delivery_contact_name || "").toLowerCase().includes(q) ||
        (o.pickup_contact_phone || "").includes(q) ||
        (o.delivery_contact_phone || "").includes(q) ||
        String(o.final_price ?? o.price ?? "").includes(q) ||
        String(o.gross_amount ?? o.gross_price ?? o.price ?? "").includes(q)
      );
    });
  }, [orders, search, opsPreset]);

  const exportCSV = () => {
    // Phase 43.16.1 — CSV export (riusa /api/admin/orders + applica filtri attuali)
    const rows = filteredOrders;
    if (!rows.length) {
      toast.error("Nessun ordine da esportare.");
      return;
    }
    const headers = [
      "id", "created_at", "status", "payment_status", "payout_status",
      "customer_name", "customer_email", "customer_phone",
      "driver_name", "driver_email",
      "pickup_address", "pickup_contact_name", "pickup_contact_phone",
      "delivery_address", "delivery_contact_name", "delivery_contact_phone",
      "package_size", "delivery_type", "distance_km", "price",
      "driver_payout_amount", "platform_fee_amount", "currency",
      "stripe_session_id", "refund_status", "refund_amount",
    ];
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => esc(r[h])).join(",")),
    ].join("\n");
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const filterSuffix = filter === "all" ? "all" : filter;
    const searchSuffix = search ? `_search-${search.replace(/[^a-z0-9]/gi, "")}` : "";
    const filename = `dapnow_orders_${filterSuffix}${searchSuffix}_${ts}.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Esportati ${rows.length} ordini.`);
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success("Stato aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-orders-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="overline">Operations</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">
            {opsPreset ? opsPreset.label : "Ordini"}
          </h1>
          {opsPreset && (
            <p className="text-sm text-dapnow-muted mt-1">
              {filteredOrders.length} ordin{filteredOrders.length === 1 ? "e" : "i"} corrispondent{filteredOrders.length === 1 ? "e" : "i"} al filtro operativo.
            </p>
          )}
          {(customerIdFilter || driverIdFilter) && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-dapnow-blue-soft border border-dapnow-blue/30 px-3 py-1 text-xs" data-testid="admin-orders-entity-filter">
              <span className="font-semibold text-dapnow-blue">
                Filtrato per {customerIdFilter ? "cliente" : "trasportatore"}
              </span>
              <button
                onClick={() => {
                  const sp = new URLSearchParams(searchParams);
                  sp.delete("customer_id"); sp.delete("driver_id");
                  setSearchParams(sp, { replace: true });
                }}
                className="text-dapnow-blue hover:text-dapnow-ink"
                data-testid="admin-orders-entity-filter-clear"
                aria-label="Rimuovi filtro"
              >
                ×
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={exportCSV}
          data-testid="admin-orders-export-csv"
          className="inline-flex items-center gap-1.5 rounded-xl border border-dapnow-line bg-white px-4 h-10 text-sm font-semibold hover:border-dapnow-blue hover:text-dapnow-blue transition"
          title="Esporta i risultati visibili in CSV"
        >
          <Download className="h-4 w-4" /> Esporta CSV
        </button>
      </div>

      {/* Phase 43.16.1 — Ricerca + filtri */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-dapnow-muted" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per ID ordine, cliente, driver, indirizzo, telefono…"
            data-testid="admin-orders-search"
            className="input-tall w-full pl-10 pr-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              data-testid="admin-orders-search-clear"
              aria-label="Pulisci"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-dapnow-bg"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="text-xs text-dapnow-muted whitespace-nowrap" data-testid="admin-orders-count">
          {loading ? "…" : `${filteredOrders.length} / ${orders.length} ordini`}
        </div>
      </div>

      {opsPreset && (
        <div
          data-testid="admin-orders-ops-filter-banner"
          className="flex items-center justify-between gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3"
        >
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0" />
            <p className="text-sm text-amber-900 truncate">
              <strong>Filtro operativo attivo:</strong> {opsPreset.label}.
              {" "}<span className="text-amber-700">I filtri di stato qui sotto sono additivi.</span>
            </p>
          </div>
          <button
            onClick={clearOpsFilter}
            data-testid="admin-orders-ops-filter-clear"
            className="inline-flex items-center gap-1 rounded-lg bg-white border border-amber-300 px-3 h-8 text-xs font-semibold hover:border-amber-500 flex-shrink-0"
          >
            <X className="h-3.5 w-3.5" /> Rimuovi filtro
          </button>
        </div>
      )}

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-2 overflow-x-auto pb-1 flex-1 min-w-0">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-orders-filter-${f}`}
              className={`shrink-0 rounded-full px-4 h-10 text-sm font-semibold transition ${
                filter === f ? "bg-dapnow-ink text-white" : "bg-white border border-dapnow-line text-dapnow-ink hover:border-dapnow-blue/40"
              }`}
            >
              {f === "all" ? "Tutti" : STATUS_LABEL[f]}
            </button>
          ))}
        </div>
        {/* Phase 44.31.19 — Toggle "Includi archiviati" */}
        <label className="inline-flex items-center gap-2 text-xs text-dapnow-muted cursor-pointer select-none shrink-0">
          <input
            type="checkbox"
            checked={includeCleanup}
            onChange={(e) => {
              setIncludeCleanup(e.target.checked);
              const sp = new URLSearchParams(searchParams);
              if (e.target.checked) sp.set("cleanup", "1"); else sp.delete("cleanup");
              setSearchParams(sp, { replace: true });
            }}
            data-testid="admin-orders-include-cleanup"
            className="h-4 w-4 rounded border-dapnow-line accent-dapnow-blue"
          />
          Includi archiviati
        </label>
      </div>

      {/* Phase 43.16 — Tabella desktop con scroll orizzontale + colonna Gestisci sticky a sinistra (Phase 43.19.2) */}
      <div className="card-soft p-0 overflow-hidden hidden lg:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: "1200px" }} data-testid="admin-orders-table">
            <thead className="bg-dapnow-bg text-left">
              <tr>
                <Th className="w-[120px] sticky left-0 bg-dapnow-bg z-10 shadow-[1px_0_0_0_rgb(228_228_231)]">Azioni</Th>
                <Th className="w-[110px]">Ordine</Th>
                <Th className="w-[140px]">Cliente</Th>
                <Th className="w-[140px]">Driver</Th>
                <Th className="min-w-[280px]">Da → A</Th>
                <Th className="w-[190px]">Importi cliente</Th>
                <Th className="w-[120px]">Pagamento</Th>
                <Th className="w-[180px]">Calcoli registrati</Th>
                <Th className="w-[120px]">Stato</Th>
                <Th className="w-[120px]">Data</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="10" className="p-10 text-center text-dapnow-muted">Caricamento…</td></tr>}
              {!loading && filteredOrders.length === 0 && (
                <tr><td colSpan="10" className="p-10 text-center text-dapnow-muted">
                  {search || opsPreset ? "Nessun ordine trovato per i filtri attivi." : "Nessun ordine."}
                </td></tr>
              )}
              {filteredOrders.map((o) => {
                const ps = o.payment_status || "pending";
                const psCls = ps === "paid" ? "bg-emerald-50 text-emerald-700"
                  : ps === "expired" || ps === "failed" || ps === "cancelled" ? "bg-red-50 text-red-700"
                  : "bg-amber-50 text-amber-700";
                return (
                <tr
                  key={o.id}
                  className={`border-t border-dapnow-line hover:bg-dapnow-bg/40 transition-colors cursor-pointer ${(o.archived || o.sub_status === "admin_cleanup_pre_pilot") ? "opacity-60" : ""}`}
                  data-testid={`admin-order-row-${o.id}`}
                  onClick={(e) => {
                    // Solo se non hanno cliccato dentro un link o un menu
                    if (e.target.closest("a, button, [role='menu'], [role='menuitem']")) return;
                    setDrawerOrder(o);
                  }}
                >
                  <Td className="sticky left-0 bg-white z-10 shadow-[1px_0_0_0_rgb(228_228_231)] group-hover:bg-dapnow-bg/40">
                    <OrderActionsMenu
                      order={o}
                      onChangeStatus={(s) => changeStatus(o.id, s)}
                      onOpenDetails={() => setDrawerOrder(o)}
                      onEdit={() => setEditOrder(o)}
                    />
                  </Td>
                  <Td className="font-mono text-xs whitespace-nowrap">
                    #{o.id.slice(0, 8).toUpperCase()}
                    {(o.archived || o.sub_status === "admin_cleanup_pre_pilot") && (
                      <span
                        className="ml-1.5 inline-flex items-center pill bg-zinc-200 text-zinc-700 text-[9px] uppercase tracking-wider"
                        title="Ordine cancellato durante la pulizia pre-pilot (reversibile)"
                        data-testid={`admin-order-cleanup-badge-${o.id}`}
                      >
                        Cleanup pre-pilot
                      </span>
                    )}
                  </Td>
                  <Td className="truncate" title={o.customer_name || ""}>
                    {o.customer_id ? (
                      <Link
                        to={`/admin/users?q=${encodeURIComponent(o.customer_email || o.customer_name || "")}`}
                        data-testid={`admin-order-customer-link-${o.id}`}
                        className="hover:text-dapnow-blue hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {o.customer_name || "—"}
                      </Link>
                    ) : (o.customer_name || "—")}
                  </Td>
                  <Td className="truncate" title={o.driver_name || ""}>
                    {o.driver_id ? (
                      <Link
                        to={`/admin/drivers/${o.driver_id}`}
                        data-testid={`admin-order-driver-link-${o.id}`}
                        className="hover:text-dapnow-blue hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {o.driver_name || "—"}
                      </Link>
                    ) : (o.driver_name || "—")}
                  </Td>
                  <Td className="min-w-[280px]" title={`${o.pickup_address || ""} → ${o.delivery_address || ""}`}>
                    <span className="block truncate">{o.pickup_address}</span>
                    <span className="block truncate text-xs text-dapnow-muted">→ {o.delivery_address}</span>
                  </Td>
                  <Td><OrderPriceSummary order={o} /></Td>
                  <Td>
                    <div className="flex flex-col gap-0.5">
                      <span className={`pill text-[10px] ${psCls} inline-flex w-fit`} data-testid={`admin-pay-pill-${o.id}`}>{ps}</span>
                      {o.payment_simulated && <span className="text-[10px] text-dapnow-muted">(simulato)</span>}
                    </div>
                  </Td>
                  <Td className="text-xs">
                    {hasRecordedCalculations(o) ? (
                      <div className="flex flex-col gap-0.5 text-[11px]">
                        <span>Calcolo driver: <strong>{formatCHF(o.driver_payout_amount)}</strong></span>
                        <span>Calcolo commissione: {formatCHF(o.platform_fee_amount)}</span>
                        <span className="text-dapnow-muted">Da riconciliare · remunerazione fuori Stripe</span>
                      </div>
                    ) : "—"}
                  </Td>
                  <Td><StatusBadge order={o} audience="admin" /></Td>
                  <Td className="text-xs text-dapnow-muted whitespace-nowrap">{formatDateTime(o.created_at)}</Td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Phase 43.19.2 — Mobile/Tablet card layout */}
      <div className="lg:hidden space-y-3" data-testid="admin-orders-cards">
        {loading && <div className="card-soft p-8 text-center text-dapnow-muted">Caricamento…</div>}
        {!loading && filteredOrders.length === 0 && (
          <div className="card-soft p-8 text-center text-dapnow-muted">
            {search || opsPreset ? "Nessun ordine trovato per i filtri attivi." : "Nessun ordine."}
          </div>
        )}
        {!loading && filteredOrders.map((o) => (
          <AdminOrderMobileCard
            key={o.id}
            order={o}
            onOpenDetails={() => setDrawerOrder(o)}
            onEdit={() => setEditOrder(o)}
            onChangeStatus={(s) => changeStatus(o.id, s)}
          />
        ))}
      </div>

      {/* Phase 43.16 — Drawer dettagli ordine (slide da destra) */}
      <OrderDetailsDrawer
        order={drawerOrder}
        onClose={() => setDrawerOrder(null)}
        onChangeStatus={(s) => drawerOrder && changeStatus(drawerOrder.id, s)}
        onEdit={() => { setEditOrder(drawerOrder); setDrawerOrder(null); }}
      />

      {/* Phase 43.17 — Modale modifica ordine */}
      {editOrder && (
        <AdminOrderEditModal
          order={editOrder}
          drivers={drivers}
          onClose={() => setEditOrder(null)}
          onSaved={() => { setEditOrder(null); load(); }}
        />
      )}
    </div>
  );
}

function OrderActionsMenu({ order, onChangeStatus, onOpenDetails, onEdit }) {
  const allStatuses = [...STATUS_STEPS, "cancelled"];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`admin-order-actions-${order.id}`}
          className="inline-flex items-center gap-1 rounded-lg border border-dapnow-line bg-white px-2.5 h-8 text-xs font-semibold text-dapnow-ink hover:border-dapnow-blue hover:text-dapnow-blue transition"
          title="Azioni rapide"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
          Gestisci
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">Cambia stato</DropdownMenuLabel>
        {allStatuses.map((s) => (
          <DropdownMenuItem
            key={s}
            onSelect={() => onChangeStatus(s)}
            data-testid={`admin-order-set-${s}`}
            className="text-xs font-medium cursor-pointer"
          >
            → {STATUS_LABEL[s]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={onEdit}
          data-testid={`admin-order-edit-${order.id}`}
          className="text-xs font-semibold cursor-pointer"
        >
          <Pencil className="h-3.5 w-3.5 mr-2" /> Modifica ordine
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onOpenDetails}
          data-testid={`admin-order-open-details-${order.id}`}
          className="text-xs font-semibold cursor-pointer text-dapnow-blue"
        >
          <ExternalLink className="h-3.5 w-3.5 mr-2" /> Apri dettagli
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OrderDetailsDrawer({ order, onClose, onChangeStatus, onEdit }) {
  if (!order) return null;
  const allStatuses = [...STATUS_STEPS, "cancelled"];
  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
        data-testid="admin-order-drawer-backdrop"
      />
      <aside
        className="fixed top-0 right-0 z-50 h-full w-full sm:w-[480px] bg-white shadow-2xl overflow-y-auto"
        data-testid="admin-order-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-dapnow-line px-5 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <p className="overline text-[10px]">Ordine</p>
            <p className="font-display text-lg font-bold tracking-tight font-mono">#{order.id.slice(0, 8).toUpperCase()}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-order-drawer-close"
            className="rounded-lg border border-dapnow-line h-9 w-9 inline-flex items-center justify-center hover:border-dapnow-blue text-sm"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-5">
          <button
            onClick={onEdit}
            data-testid="admin-order-drawer-edit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-dapnow-ink text-white px-3 h-10 text-sm font-semibold hover:bg-dapnow-blue transition"
          >
            <Pencil className="h-4 w-4" /> Modifica ordine
          </button>

          <section>
            <p className="overline mb-2">Stato & cliente</p>
            <div className="space-y-1.5 text-sm">
              <KV k="Stato" v={<StatusBadge order={order} audience="admin" />} />
              <KV k="Cliente" v={order.customer_name || "—"} />
              <KV k="Driver" v={order.driver_name || "—"} />
              <KV k="Creato il" v={formatDateTime(order.created_at)} />
            </div>
          </section>

          <section>
            <p className="overline mb-2">Tragitto</p>
            <div className="text-sm space-y-1">
              <p className="font-medium">{order.pickup_address}</p>
              <p className="text-dapnow-muted text-xs">→ {order.delivery_address}</p>
            </div>
          </section>

          {/* Phase 44.31.14 — Posizione driver live / ultima nota */}
          {order.driver_id && (
            <DriverLocationSection orderId={order.id} orderStatus={order.status} />
          )}

          <section>
            <p className="overline mb-2">Pagamento</p>
            <div className="space-y-1.5 text-sm">
              <OrderPriceSummary order={order} />
              <KV k="Stato" v={order.payment_status || "pending"} />
              {order.payment_simulated && <KV k="Modalità" v="simulato" />}
              {order.stripe_session_id && (
                <p className="text-[11px] font-mono text-dapnow-muted break-all pt-1" data-testid={`admin-order-sid-${order.id}`}>
                  stripe_session: {order.stripe_session_id}
                </p>
              )}
            </div>
          </section>

          {hasRecordedCalculations(order) && (
            <section data-testid="admin-order-recorded-calculations">
              <p className="overline mb-2">Calcoli registrati · da riconciliare</p>
              <div className="space-y-1.5 text-sm">
                <KV k="Calcolo driver" v={formatCHF(order.driver_payout_amount)} />
                <KV k="Calcolo commissione" v={formatCHF(order.platform_fee_amount)} />
                {order.payout_status && <KV k="Stato storico registrato" v={order.payout_status} />}
              </div>
              <p className="mt-2 text-xs text-dapnow-muted">
                La remunerazione dei driver interni è gestita fuori da Stripe. Questi calcoli storici non definiscono il compenso concordato e non attestano un pagamento o un trasferimento programmato.
              </p>
            </section>
          )}

          <section>
            <p className="overline mb-2">Cambia stato</p>
            <div className="flex gap-2 flex-wrap" data-testid="admin-order-drawer-status-buttons">
              {allStatuses.map((s) => (
                <button
                  key={s}
                  onClick={() => onChangeStatus(s)}
                  data-testid={`admin-order-drawer-set-${s}`}
                  disabled={s === order.status}
                  className={`rounded-full px-3 h-9 text-xs font-semibold border transition ${
                    s === order.status
                      ? "bg-dapnow-ink text-white border-dapnow-ink cursor-default"
                      : "bg-white border-dapnow-line hover:border-dapnow-blue"
                  }`}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </section>

          {order.pickup_photo_url && (
            <section data-testid={`admin-order-pickup-photo-${order.id}`}>
              <p className="overline mb-2">Foto ritiro</p>
              <ProofPhotoView orderId={order.id} kind="pickup" testid={`admin-pickup-photo-${order.id}`} defaultExpanded />
            </section>
          )}

          {order.status === "delivered" && (
            <section>
              <p className="overline mb-2">Prova di consegna</p>
              {order.delivery_photo_url ? (
                <div data-testid={`admin-order-pod-${order.id}`}>
                  <DeliveryPhotoView orderId={order.id} testid={`admin-pod-${order.id}`} />
                </div>
              ) : (
                <p className="text-xs text-amber-700 inline-flex items-center gap-1.5" data-testid={`admin-order-pod-missing-${order.id}`}>
                  <Camera className="h-3.5 w-3.5" /> Nessuna foto consegna caricata dal driver.
                </p>
              )}
            </section>
          )}
        </div>
      </aside>
    </>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between items-center gap-3">
      <span className="text-xs text-dapnow-muted">{k}</span>
      <span className="text-sm font-medium text-right truncate">{v}</span>
    </div>
  );
}

function hasRecordedCalculations(order) {
  return order.driver_payout_amount != null || order.platform_fee_amount != null;
}

function OrderPriceSummary({ order }) {
  const gross = order.gross_amount ?? order.gross_price ?? order.price;
  const cash = order.final_price ?? order.price ?? order.gross_amount;
  const fromPack = order.payment_source === "delivery_pack";
  const fromSubscription = !fromPack && (order.included_delivery_consumed || order.payment_source === "subscription_full_credit");
  return (
    <div className="space-y-1 text-xs" data-testid="admin-order-price-summary">
      <div data-testid="admin-order-gross" className="text-dapnow-muted">Prezzo lordo: {formatCHF(gross)}</div>
      <div data-testid="admin-order-cash" className="font-semibold text-dapnow-ink">Importo finale in denaro: {formatCHF(cash)}</div>
      {Number(order.credit_balance_applied_chf) > 0 && (
        <div data-testid="admin-order-wallet">Credito wallet applicato: {formatCHF(order.credit_balance_applied_chf)}</div>
      )}
      {fromPack && <div data-testid="admin-order-pack">Copertura registrata: pacchetto corse</div>}
      {fromSubscription && <div data-testid="admin-order-subscription">Copertura registrata: abbonamento</div>}
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`px-4 py-3 overline text-[10px] font-semibold ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

// Phase 44.31.14 — Sezione posizione driver per admin OrderDetailsDrawer.
// Mostra: live (push GPS dall'ordine attivo) o ultima posizione nota (driver_info)
// con staleness, accuracy, link Google Maps, fase ordine, distanza dalla destinazione,
// "driver fermo da X min" e mini-mappa statica.
// Polling: 25s per ordini attivi (accepted/picking_up/picked_up/delivering),
// 90s per ordini in altri stati (no polling aggressivo).
function DriverLocationSection({ orderId, orderStatus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const { data: d } = await api.get(`/admin/orders/${orderId}/driver-location`);
        if (alive) { setData(d); setErr(null); }
      } catch (e) {
        if (alive) setErr(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchOnce();
    const activeStatuses = ["accepted", "picking_up", "picked_up", "delivering"];
    const intervalMs = activeStatuses.includes(orderStatus) ? 25000 : 90000;
    timer = setInterval(fetchOnce, intervalMs);
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [orderId, orderStatus]);

  if (loading) {
    return (
      <section data-testid={`admin-order-driver-loc-loading-${orderId}`}>
        <p className="overline mb-2">Posizione pacco / ultimo GPS driver</p>
        <p className="text-xs text-dapnow-muted">Caricamento posizione…</p>
      </section>
    );
  }
  if (err) {
    return (
      <section data-testid={`admin-order-driver-loc-error-${orderId}`}>
        <p className="overline mb-2">Posizione pacco / ultimo GPS driver</p>
        <p className="text-xs text-dapnow-red">Errore: {err}</p>
      </section>
    );
  }
  if (!data) return null;

  const {
    best, is_live, is_stale, has_any_location, current, last_known,
    phase_label, distance_to_target_km, target,
    stopped_since_minutes,
  } = data;

  // Etichetta stato tracking (copy admin)
  let trackingChip = null;
  if (!has_any_location) {
    trackingChip = { label: "GPS non disponibile", cls: "bg-zinc-100 text-dapnow-muted", Icon: WifiOff };
  } else if (is_live) {
    trackingChip = { label: `Live · aggiornato ${formatAge(best.age_seconds)}`, cls: "bg-emerald-50 text-emerald-700", Icon: Wifi };
  } else if (best.age_seconds != null && best.age_seconds < 300) {
    trackingChip = { label: `Aggiornato ${formatAge(best.age_seconds)}`, cls: "bg-amber-50 text-amber-800", Icon: WifiOff };
  } else if (is_stale) {
    trackingChip = { label: `Posizione non recente · ${best.age_seconds != null ? formatAge(best.age_seconds) : "—"}`, cls: "bg-red-50 text-red-700", Icon: WifiOff };
  }

  // Mini-mappa: Google Maps embed (gratuito, senza API key per modalità "?q=" + zoom)
  // Marker singolo sulla "best" position; se c'è target con coordinate, mostra anche destinazione
  const mapUrl = has_any_location
    ? `https://www.google.com/maps?q=${best.lat},${best.lng}&z=14&output=embed`
    : null;

  return (
    <section data-testid={`admin-order-driver-location-${orderId}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="overline">Posizione pacco / ultimo GPS driver</p>
        {trackingChip && (
          <span
            className={`inline-flex items-center gap-1 pill ${trackingChip.cls} text-[10px]`}
            data-testid="admin-driver-loc-tracking-state"
          >
            <trackingChip.Icon className="h-3 w-3" /> {trackingChip.label}
          </span>
        )}
      </div>

      {/* Fase ordine + distanza target */}
      {phase_label && (
        <div className="flex items-center justify-between gap-3 text-xs mb-2" data-testid="admin-driver-loc-phase">
          <span className="inline-flex items-center gap-1 text-dapnow-muted">
            <Navigation className="h-3 w-3" />
            Fase: <strong className="text-dapnow-ink">{phase_label}</strong>
          </span>
          {distance_to_target_km != null && target && (
            <span className="text-dapnow-muted" data-testid="admin-driver-loc-distance">
              ~{distance_to_target_km.toFixed(1)} km da {target.kind === "pickup" ? "ritiro" : "consegna"}
            </span>
          )}
        </div>
      )}

      {/* Warning "driver fermo" */}
      {stopped_since_minutes != null && stopped_since_minutes >= 2 && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-2 mb-2 text-[11px] text-amber-800 inline-flex items-start gap-1.5"
          data-testid="admin-driver-loc-stopped-warning"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            <strong>Driver fermo da {stopped_since_minutes < 60 ? `${Math.round(stopped_since_minutes)} min` : `${(stopped_since_minutes/60).toFixed(1)} h`}</strong>
            {" "}— GPS in posizione costante. Verifica se sosta/problema.
          </span>
        </div>
      )}

      {/* Warning "GPS vecchio" se best.age > 5 min e ordine attivo */}
      {has_any_location && is_stale && best.age_seconds != null && best.age_seconds > 300 && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 p-2 mb-2 text-[11px] text-red-800 inline-flex items-start gap-1.5"
          data-testid="admin-driver-loc-stale-warning"
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            Ultimo GPS più vecchio di 5 min. Il driver potrebbe avere app chiusa, no signal o problema operativo.
          </span>
        </div>
      )}

      {!has_any_location ? (
        <p className="text-xs text-dapnow-muted rounded-xl border border-dapnow-line bg-zinc-50 p-3" data-testid="admin-driver-loc-empty">
          GPS non disponibile per questo driver. Nessun ping ricevuto né dall&apos;ordine né dall&apos;app driver.
        </p>
      ) : (
        <div className="space-y-2">
          {/* Mini-mappa */}
          {mapUrl && (
            <div
              className="rounded-xl overflow-hidden border border-dapnow-line bg-zinc-100"
              data-testid="admin-driver-loc-mini-map"
            >
              <iframe
                src={mapUrl}
                width="100%"
                height="180"
                style={{ border: 0, display: "block" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mini mappa posizione driver"
              />
            </div>
          )}

          {/* Card posizione "migliore" usabile */}
          <div className={`rounded-xl border p-3 ${
            is_live ? "border-emerald-200 bg-emerald-50/40"
            : is_stale ? "border-red-200 bg-red-50/40"
            : "border-amber-200 bg-amber-50/40"
          }`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-dapnow-muted">
                  {best.source === "live_order_ping" ? "Live · ordine in corso"
                    : best.source === "stale_order_ping" ? "Ultimo ping ordine"
                    : "Ultima posizione driver (fuori ordine)"}
                </p>
                <p className="font-mono text-xs mt-1" data-testid="admin-driver-loc-coords">
                  {best.lat.toFixed(5)}, {best.lng.toFixed(5)}
                </p>
                <p className="text-[11px] text-dapnow-muted mt-1">
                  {best.age_seconds == null ? "—" : formatAge(best.age_seconds)}
                  {best.accuracy_m != null && ` · ±${Math.round(best.accuracy_m)}m`}
                  {best.speed_kmh != null && ` · ${Math.round(best.speed_kmh)} km/h`}
                </p>
              </div>
              <a
                href={best.maps_url}
                target="_blank"
                rel="noreferrer"
                data-testid="admin-driver-loc-maps-link"
                className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-dapnow-line bg-white px-2 h-8 text-[11px] font-semibold hover:border-dapnow-blue hover:text-dapnow-blue"
              >
                <Navigation className="h-3 w-3" /> Maps
              </a>
            </div>
          </div>

          {/* Se "best" è l'order ping ma c'è anche un last_known diverso più "live" */}
          {current && last_known && best.source !== "driver_last_known" && (
            <details className="rounded-xl border border-dapnow-line bg-white p-2 text-xs" data-testid="admin-driver-loc-last-known-block">
              <summary className="cursor-pointer text-dapnow-muted hover:text-dapnow-ink">
                Confronta con ultima posizione driver (fuori ordine)
              </summary>
              <div className="mt-2">
                <p className="font-mono text-[11px] text-dapnow-muted">
                  {last_known.lat.toFixed(5)}, {last_known.lng.toFixed(5)}
                </p>
                <p className="text-[11px] text-dapnow-muted">
                  {last_known.age_seconds == null ? "—" : formatAge(last_known.age_seconds)}
                </p>
                <a
                  href={last_known.maps_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-dapnow-blue hover:underline mt-1"
                >
                  <MapPin className="h-3 w-3" /> Apri in Maps
                </a>
              </div>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

function formatAge(s) {
  if (s == null) return "—";
  if (s < 60) return `${s} sec fa`;
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return `${Math.floor(s / 86400)} g fa`;
}

// Phase 43.19.2 — Card mobile per admin orders (sostituisce tabella su < lg)
function AdminOrderMobileCard({ order, onOpenDetails, onEdit, onChangeStatus }) {
  const o = order;
  const ps = o.payment_status || "pending";
  const psCls = ps === "paid" ? "bg-emerald-50 text-emerald-700"
    : ps === "expired" || ps === "failed" || ps === "cancelled" ? "bg-red-50 text-red-700"
    : "bg-amber-50 text-amber-700";
  return (
    <div
      className={`card-soft p-4 cursor-pointer hover:border-dapnow-blue/40 ${(o.archived || o.sub_status === "admin_cleanup_pre_pilot") ? "opacity-60" : ""}`}
      onClick={(e) => {
        if (e.target.closest("a, button, [role='menu'], [role='menuitem']")) return;
        onOpenDetails();
      }}
      data-testid={`admin-order-mobile-card-${o.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[11px] text-dapnow-muted">
            #{o.id.slice(0, 8).toUpperCase()}
            {(o.archived || o.sub_status === "admin_cleanup_pre_pilot") && (
              <span className="ml-1.5 inline-flex items-center pill bg-zinc-200 text-zinc-700 text-[9px] uppercase tracking-wider">
                Cleanup pre-pilot
              </span>
            )}
          </p>
          <p className="font-semibold truncate">{o.customer_name || "—"}</p>
        </div>
        <StatusBadge order={o} audience="admin" />
      </div>
      <div className="text-sm text-dapnow-muted mb-3">
        <p className="truncate">📍 {o.pickup_address}</p>
        <p className="truncate">🏁 {o.delivery_address}</p>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2 flex-wrap">
          <OrderPriceSummary order={o} />
          <span className={`pill text-[10px] ${psCls}`}>{ps}</span>
          {o.driver_name && <span className="text-dapnow-muted truncate">· {o.driver_name}</span>}
        </div>
        <OrderActionsMenu
          order={o}
          onChangeStatus={onChangeStatus}
          onOpenDetails={onOpenDetails}
          onEdit={onEdit}
        />
      </div>
    </div>
  );
}
